import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router';
import type { RouteComponentProps } from 'react-router';
import { Check, ChevronLeft, ChevronRight, User } from 'lucide-react';
import {
  COVER_ANIMALS,
  DECISION_QUESTIONS,
  FAMILY_QUESTIONS,
  INTEREST_QUESTION,
  LIFE_QUESTIONS,
  PERSONALITY_QUESTIONS,
  ROLE_OPTIONS,
  SERVICE_QUESTIONS,
  TRUE_FALSE_QUESTIONS,
  coverQuizSignals,
  knowledgeBadge,
  optionsFor
} from './coverQuizData';
import type { CoverPersona, CoverQuizAnswers, CoverRole, MultiQuestion, PickQuestion } from './coverQuizData';
import { coverClientReport } from './coverQuizReport';
import { parseQuizLink } from './coverQuizStorage';
import { addPublicQuizGoals, submitPublicQuiz } from '../../store/clientQuiz/api';
import { isTerminalLinkState, LinkMessage, RetryBanner, usePublicQuizLink, useQuizSubmit } from './publicQuizLink';
import type { LinkState } from './publicQuizLink';
import { draftLang, linkHasEnded, quizDraftKey, readQuizDraft, useQuizDraft } from './quizDraft';
import { L, QuizLangProvider, QuizLangSwitcher, usePageQuizLang, useTx } from './quizLang';
import type { QuizLang, Txt } from './quizLang';
import {
  DEMO_INFLATION,
  DEMO_RETURN,
  GOALS,
  GOAL_MAX,
  INCOME_QUESTION,
  MONTHLY_QUESTION,
  SAVED_QUESTION,
  SUGGESTED_SAVING_RATE,
  formatMonthly,
  formatWan,
  goalLabel,
  goalsFor,
  planGoals
} from './coverGoalData';
import type { CoverGoalAnswers, GoalDef, GoalQuestion } from './coverGoalData';
import '../CareerQuiz/CareerQuiz.scss';
import './CoverQuiz.scss';
import DiscLetterBadge from '../../components/DiscLetterBadge/DiscLetterBadge';

/**
 * 保障小測驗 — the public, no-login page a client plays (route /b/<token>, preview /b).
 * Question content and the scoring behind it: ./coverQuizData.ts.
 *
 * Borrows CareerQuiz's page shell and card styles (`career-quiz-page`, `cq-*`)
 * so the two quizzes an adviser sends look like one family.
 */

// 0 歡迎 · 1 身分 · 2 性格 · 3 真假 · 4 生活 · 5 作決定 · 6 想了解 · 7 家庭 · 8 服務方式 · 9 結果
// (滑一滑 was dropped on 2026-10-01: it only leaned the DISC letter, which 性格快問 already asks.)
const STEP_LABELS: Txt[] = [
  L('歡迎', '欢迎', 'Welcome'),
  L('你的身分', '你的身分', 'About you'),
  L('性格快問', '性格快问', 'Quick personality'),
  L('真假挑戰', '真假挑战', 'True or false'),
  L('生活快問', '生活快问', 'Quick life questions'),
  L('作決定的方式', '作决定的方式', 'How you decide'),
  L('最想了解的範疇', '最想了解的范畴', 'What you want to learn'),
  L('生活與家庭', '生活与家庭', 'Life and family'),
  L('最後一步：服務方式', '最后一步：服务方式', 'Last step: how you like to be served'),
  L('你的測驗結果', '你的测验结果', 'Your quiz results'),
  // 第二部分
  L('選擇你的目標', '选择你的目标', 'Pick your goals'),
  L('目標細節', '目标细节', 'Goal details'),
  L('儲蓄情況', '储蓄情况', 'Your savings'),
  L('目標參考報告', '目标参考报告', 'Goal report')
];
const FAMILY_STEP = 7;
const SERVICE_STEP = 8;
const RESULT_STEP = 9;
const GOAL_PICK_STEP = 10;
const GOAL_DETAIL_STEP = 11;
const GOAL_SAVING_STEP = 12;
const REPORT_STEP = 13;
const EMPTY_GOALS: CoverGoalAnswers = { picked: [], details: {} };

/** What coming back to the link brings back (quizDraft useQuizDraft). */
type CoverDraft = {
  step: number;
  name: string;
  lang: string;
  persona?: CoverPersona;
  role?: CoverRole;
  picks: Record<string, string>;
  multi: Record<string, string[]>;
  trueFalse: Record<string, boolean>;
  tfIndex: number;
  goals: CoverGoalAnswers;
  /** When part 1 was sent from this device — 第二部分 can still be added for a day after. */
  sentAt?: number | null;
  /** The invitation's adviser, kept because a resumed link no longer returns the quiz. */
  adviserName?: string;
};

/**
 * How long after part 1 the server still takes 第二部分 on the same link
 * (server utils/client-quiz.ts CLIENT_QUIZ_LIMITS.goalsWindowMs).
 */
const GOALS_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Where the page starts: the saved draft, or a fresh quiz. */
type CoverStart = Omit<CoverDraft, 'lang' | 'sentAt' | 'adviserName'> & {
  lang: QuizLang | null;
  sentAt: number | null;
  adviserName: string;
  /** The client's name came from the draft, so the invitation's must not replace it. */
  hasName: boolean;
};

/*
 * The draft's unpacking and the decisions below live outside the component on
 * purpose: this repo's react-hooks lint counts every branch in the component
 * body, and CoverQuizComponent's JSX already has so many that a few more `?.` /
 * `??` above the hooks make it report them as called conditionally.
 */
const coverStartOf = (draft: Partial<CoverDraft> | null): CoverStart => ({
  step: draft?.step ?? 0,
  name: draft?.name ?? '',
  hasName: draft?.name !== undefined,
  lang: draftLang(draft?.lang),
  persona: draft?.persona,
  role: draft?.role,
  picks: draft?.picks ?? {},
  multi: draft?.multi ?? {},
  trueFalse: draft?.trueFalse ?? {},
  tfIndex: draft?.tfIndex ?? 0,
  goals: draft?.goals ?? EMPTY_GOALS,
  sentAt: draft?.sentAt ?? null,
  adviserName: draft?.adviserName ?? ''
});

/**
 * Part 1 was sent from this device and 第二部分 is still open on the same link:
 * the server now answers 已完成, but the client is on their result or half-way
 * through 第二部分 — so the page carries on there instead of shutting them out.
 */
const linkStateOf = (serverLink: LinkState, sentAt: number | null): LinkState =>
  serverLink === 'done' && sentAt !== null && Date.now() - sentAt < GOALS_WINDOW_MS ? 'ready' : serverLink;

/**
 * Until part 1 is sent, the draft is kept for whenever the client comes back.
 * Once part 1 is sent from here (`sentAt`), it is kept through the 第二部分
 * window, so coming back lands on the result or the goals rather than 已完成.
 * 第二部分 sent on its own (no `sentAt`) has nothing after it: dropped once sent.
 */
const coverDraftPolicy = (link: LinkState, saved: boolean, sentAt: number | null) => ({
  save: link === 'ready' && (!saved || sentAt !== null),
  clear: (saved && sentAt === null) || linkHasEnded(link)
});
const CONFETTI = ['🎉', '✨', '🎊', '⭐', '💫', '🎉', '✨', '🎊'];

/**
 * The welcome page lists what the client gets back — a few words each, in the
 * order part 1 delivers them. 目標參考報告 is 第二部分, offered after the
 * result, so it comes last and is marked optional.
 */
const WELCOME_VALUES = [
  {
    icon: '💰',
    tone: 'violet',
    title: L('理財性格與小建議', '理财性格与小建议', 'Your money personality and tips'),
    desc: L('看看你怎樣作理財決定', '看看你怎样作理财决定', 'See how you make money decisions')
  },
  {
    icon: '🧠',
    tone: 'amber',
    title: L('理財常識挑戰', '理财常识挑战', 'Money know-how challenge'),
    desc: L('幾條真假題，每題附解釋', '几条真假题，每题附解释', 'A few true-or-false questions, each explained')
  },
  {
    icon: '🎯',
    tone: 'green',
    title: L('目標參考報告（可選）', '目标参考报告（可选）', 'Goal report (optional)'),
    desc: L('升學、置業、退休要存多少', '升学、置业、退休要存多少', 'What to save for school, a home or retirement')
  }
];

const PART_TWO_VALUES = [
  {
    icon: '🎯',
    tone: 'green',
    title: L('目標所需金額', '目标所需金额', 'What your goals cost'),
    desc: L('將來大約要準備多少', '将来大约要准备多少', "Roughly how much you'll need")
  },
  {
    icon: '💰',
    tone: 'violet',
    title: L('每月儲蓄參考', '每月储蓄参考', 'Monthly savings guide'),
    desc: L('現在每月要存多少', '现在每月要存多少', 'How much to save each month')
  },
  {
    icon: '📊',
    tone: 'amber',
    title: L('同類人士參考', '同类人士参考', 'How others prepare'),
    desc: L('同類情況的人一般怎樣準備', '同类情况的人一般怎样准备', 'How people like you usually prepare')
  }
];

export type CoverQuizProps = RouteComponentProps;

/** The page's own words, beside the question data. */
const COPY = {
  gutFeel: L('憑直覺選，不用想太久 ⚡', '凭直觉选，不用想太久 ⚡', "Go with your gut, don't overthink it ⚡"),
  back: L('上一步', '上一步', 'Back'),
  next: L('下一步', '下一步', 'Next'),
  yourAdviser: L('你的顧問', '你的顾问', 'your adviser'),
  adviserNamed: (name: string) => L(`${name}顧問`, `${name}顾问`, `your adviser ${name}`),
  brand: L('PortfoPlus 小測驗', 'PortfoPlus 小测验', 'PortfoPlus quiz'),
  invites: (adviser: string) => L(`${adviser}邀請你參加`, `${adviser}邀请你参加`, `Invited by ${adviser}`),
  partTwo: L('🎯 第二部分', '🎯 第二部分', '🎯 Part 2'),
  game: L('⚡ 小遊戲', '⚡ 小游戏', '⚡ Mini game'),
  done: L('完成 🎉', '完成 🎉', 'Done 🎉'),
  progress: L('測驗進度', '测验进度', 'Quiz progress'),
  aboutOneMinute: L('約 1 分鐘', '约 1 分钟', 'About 1 minute'),
  // Time only, never how many questions: a count up front reads as a chore.
  aboutTwoMinutes: L('約 2 分鐘', '约 2 分钟', 'About 2 minutes'),
  partTwoTitle: L('1 分鐘算清理財目標', '1 分钟算清理财目标', 'Work out your money goals in 1 minute'),
  partOneTitle: L('2 分鐘理財小測驗', '2 分钟理财小测验', '2-minute money quiz'),
  partTwoSubtitle: L(
    '選一至兩個目標，看看每月要存多少',
    '选一至两个目标，看看每月要存多少',
    'Pick one or two goals and see what to save each month'
  ),
  partOneSubtitle: L('輕鬆作答，完成即看結果', '轻松作答，完成即看结果', 'Easy questions, results right away'),
  youWillGet: L('完成後你會得到', '完成后你会得到', "What you'll get"),
  hello: (name: string) => L(`你好，${name}！`, `你好，${name}！`, `Hi ${name}!`),
  nameLabel: L('你的稱呼（選填）', '你的称呼（选填）', 'Your name (optional)'),
  namePlaceholder: L('例如：David / 晴天', '例如：David / 晴天', 'e.g. David / Sunny'),
  start: L('開始測驗', '开始测验', 'Start'),
  roleTitle: L('先選一個最貼近你的身分', '先选一个最贴近你的身分', 'First, pick what describes you best'),
  roleDesc: L(
    '之後的題目會按你的身分調整',
    '之后的题目会按你的身分调整',
    'The questions that follow will fit your answer'
  ),
  personalityLabel: L('性格快問 · 憑直覺選', '性格快问 · 凭直觉选', 'Quick personality · go with your gut'),
  tfKicker: L('真假挑戰', '真假挑战', 'True or false'),
  tfIntro: L(
    '考考你的理財常識！答錯也沒關係，每題都有簡單解釋 😉',
    '考考你的理财常识！答错也没关系，每题都有简单解释 😉',
    'Test your money know-how! Wrong answers are fine: each one comes with a simple explanation 😉'
  ),
  quote: (text: string) => L(`「${text}」`, `「${text}」`, `"${text}"`),
  tfTrue: L('⭕ 真', '⭕ 真', '⭕ True'),
  tfFalse: L('❌ 假', '❌ 假', '❌ False'),
  correct: L('🎉 答對了！', '🎉 答对了！', '🎉 Correct!'),
  almost: (answer: boolean) =>
    answer
      ? L('差一點！答案是「真」', '差一点！答案是「真」', 'So close! The answer is "True"')
      : L('差一點！答案是「假」', '差一点！答案是「假」', 'So close! The answer is "False"'),
  nextQuestion: L('下一題', '下一题', 'Next question'),
  continue: L('繼續', '继续', 'Continue'),
  lifeLabel: L('生活快問', '生活快问', 'Quick life questions'),
  decisionLabel: L('作決定的方式', '作决定的方式', 'How you decide'),
  pickUpTo: (max: number, picked: number) =>
    L(
      `最多選 ${max} 個 · 已選 ${picked} / ${max}`,
      `最多选 ${max} 个 · 已选 ${picked} / ${max}`,
      `Pick up to ${max} · ${picked} / ${max} picked`
    ),
  pickSeveral: L('可以選多個', '可以选多个', 'Pick as many as apply'),
  serviceTitle: L('你希望顧問怎樣服務你？', '你希望顾问怎样服务你？', 'How would you like your adviser to serve you?'),
  serviceDesc: (adviser: string) =>
    L(
      `${adviser}想了解最適合你的方式，沒有對錯 😊`,
      `${adviser}想了解最适合你的方式，没有对错 😊`,
      `${capitalise(adviser)} would like to know what suits you best. There's no wrong answer 😊`
    ),
  consentReport: (adviser: string) =>
    L(
      `按「看我的測驗結果」即表示你同意把結果分享給${adviser}，方便準備合適的資料。`,
      `按「看我的测验结果」即表示你同意把结果分享给${adviser}，方便准备合适的资料。`,
      `By tapping "See my results" you agree to share your results with ${adviser}, so they can prepare suitable information.`
    ),
  seeReport: L('看我的測驗結果 ✨', '看我的测验结果 ✨', 'See my results ✨'),
  reportOf: (name: string) =>
    name
      ? L(`${name} 的測驗結果`, `${name} 的测验结果`, `${name}'s quiz results`)
      : L('你的測驗結果', '你的测验结果', 'Your quiz results'),
  knowledge: L('常識挑戰', '常识挑战', 'Know-how'),
  personality: L('理財性格', '理财性格', 'Money personality'),
  priorities: L('🧭 為你整理的重點', '🧭 为你整理的重点', '🧭 Your key points'),
  yourPersonality: L('你的理財性格：', '你的理财性格：', 'Your money personality: '),
  serviceHeading: L('🤝 你希望的服務方式', '🤝 你希望的服务方式', '🤝 How you like to be served'),
  serviceSaved: (adviser: string) =>
    L(
      `${adviser}會參考你的喜好，安排之後的聯絡和檢視。`,
      `${adviser}会参考你的喜好，安排之后的联络和检视。`,
      `${capitalise(adviser)} will follow your preferences for future contact and reviews.`
    ),
  sharedPartOne: (adviser: string) =>
    L(
      `測驗結果已分享給${adviser}。見面時可以按這些重點一起詳談 😊`,
      `测验结果已分享给${adviser}。见面时可以按这些重点一起详谈 😊`,
      `Your quiz results have been shared with ${adviser}. You can go through these points together when you meet 😊`
    ),
  trial: L(
    '這是試玩版，結果只顯示在你的手機。',
    '这是试玩版，结果只显示在你的手机。',
    'This is a trial: the results only show on your phone.'
  ),
  partTwoAsk: L(
    '🎯 想知道你的目標要準備多少？',
    '🎯 想知道你的目标要准备多少？',
    '🎯 Want to know how much your goals need?'
  ),
  partTwoPitch: L(
    '揀一至兩個目標，看看要準備多少、每月大約要存多少（約 1 分鐘）',
    '拣一至两个目标，看看要准备多少、每月大约要存多少（约 1 分钟）',
    'Pick one or two goals to see how much you need and roughly how much to save each month (about 1 minute)'
  ),
  partTwoPitchDream: (dream: string) =>
    L(
      `你說十年後想「${dream}」。揀一至兩個目標，看看要準備多少、每月大約要存多少（約 1 分鐘）`,
      `你说十年后想「${dream}」。拣一至两个目标，看看要准备多少、每月大约要存多少（约 1 分钟）`,
      `You said in ten years you'd like: "${dream}". Pick one or two goals to see how much you need and roughly how much to save each month (about 1 minute)`
    ),
  startPartTwo: L('開始第二部分', '开始第二部分', 'Start part 2'),
  restart: L('重新測驗', '重新测验', 'Start again'),
  goalsTitle: L('你最想達成哪些目標？', '你最想达成哪些目标？', 'Which goals matter most to you?'),
  backToResult: L('返回結果', '返回结果', 'Back to results'),
  consentGoals: (adviser: string) =>
    L(
      `按「看參考報告」即表示你同意把結果分享給${adviser}，方便準備合適的資料。`,
      `按「看参考报告」即表示你同意把结果分享给${adviser}，方便准备合适的资料。`,
      `By tapping "See goal report" you agree to share your results with ${adviser}, so they can prepare suitable information.`
    ),
  seeGoalReport: L('看參考報告 📊', '看参考报告 📊', 'See goal report 📊'),
  goalReportOf: (name: string) =>
    name
      ? L(`${name} 的目標參考報告`, `${name} 的目标参考报告`, `${name}'s goal report`)
      : L('你的目標參考報告', '你的目标参考报告', 'Your goal report'),
  today: L('一般參考（今天）', '一般参考（今天）', 'Typical cost (today)'),
  about: L('約', '约', 'About'),
  inYears: (years: number) =>
    L(`${years} 年後需要（計通脹）`, `${years} 年后需要（计通胀）`, `Needed in ${years} years (with inflation)`),
  monthly: L('每月大約要存', '每月大约要存', 'Save each month, roughly'),
  enough: L('已準備足夠 🎉', '已准备足够 🎉', 'Already covered 🎉'),
  facts: L('📊 參考數據', '📊 参考数据', '📊 Reference figures'),
  typical: L('💡 一般情況', '💡 一般情况', '💡 What people usually do'),
  coverage: L(
    '按你現在每月可存的金額，大約可以達到目標的',
    '按你现在每月可存的金额，大约可以达到目标的',
    'At what you can save each month now, you would reach about'
  ),
  savingRate: L('💰 儲蓄比例參考', '💰 储蓄比例参考', '💰 Savings rate guide'),
  savingNow: L('你現在每月可存', '你现在每月可存', 'You can save each month'),
  ofIncome: (pct: number) => L(`約收入的 ${pct}%`, `约收入的 ${pct}%`, `About ${pct}% of income`),
  goalsNeed: L('這些目標每月需要', '这些目标每月需要', 'These goals need each month'),
  usual: L('一般建議', '一般建议', 'Usual suggestion'),
  usualRate: (pct: number) => L(`收入的 ${pct}% 或以上`, `收入的 ${pct}% 或以上`, `${pct}% of income or more`),
  disclaimer: (inflation: number, ret: number) =>
    L(
      `⚠️ 示範數字，僅供參考，不構成任何投資建議。假設每年通脹 ${inflation}%、儲蓄每年回報 ${ret}%；實際情況因人而異。`,
      `⚠️ 示范数字，仅供参考，不构成任何投资建议。假设每年通胀 ${inflation}%、储蓄每年回报 ${ret}%；实际情况因人而异。`,
      `⚠️ Demo figures for reference only, not investment advice. Assumes ${inflation}% inflation and a ${ret}% yearly return on savings; real situations vary.`
    ),
  sharedGoals: (adviser: string) =>
    L(
      `報告已一併保存，${adviser}可以幫你詳細規劃 😊`,
      `报告已一并保存，${adviser}可以帮你详细规划 😊`,
      `Your report has been saved too, and ${adviser} can help you plan in detail 😊`
    ),
  goalsLocal: L(
    '報告只顯示在你的手機。想詳細規劃，可以找你的顧問聊聊。',
    '报告只显示在你的手机。想详细规划，可以找你的顾问聊聊。',
    'The report only shows on your phone. For a detailed plan, talk to your adviser.'
  ),
  repick: L('重新選擇目標', '重新选择目标', 'Pick goals again'),
  backToReport: L('返回我的測驗結果', '返回我的测验结果', 'Back to my quiz results')
};

/** English sentences start with the adviser label (「your adviser …」). */
const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const stagger = (idx: number): React.CSSProperties => ({ animationDelay: `${Math.min(idx * 45, 360)}ms` });

/** One pick question at a time; picking advances to the next, the last one hands on. */
const PickSeries = (props: {
  questions: PickQuestion[];
  persona?: CoverPersona;
  picks: Record<string, string>;
  onPick: (id: string, key: string) => void;
  onDone: () => void;
  onBack: () => void;
  label: string;
}) => {
  const { questions, persona, picks, onPick, onDone, onBack, label } = props;
  const t = useTx();
  const firstOpen = questions.findIndex(q => !picks[q.id]);
  const [index, setIndex] = useState(firstOpen < 0 ? 0 : firstOpen);
  const current = questions[index];

  // The advance waits a beat so the picked card can flash. One pending advance
  // at most, and none that outlives the step or a tap on 上一步.
  const advanceTimer = useRef<number | null>(null);
  const cancelAdvance = () => {
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
  };
  useEffect(() => cancelAdvance, []);

  const pick = (key: string) => {
    onPick(current.id, key);
    cancelAdvance();
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null;
      if (index < questions.length - 1) setIndex(index + 1);
      else onDone();
    }, 280);
  };
  const back = () => {
    cancelAdvance();
    if (index > 0) setIndex(index - 1);
    else onBack();
  };

  return (
    <div className="cq-card cq-card-rapid" data-testid={`cover-quiz-series-${questions[0].id}`}>
      <div className="cq-rapid-dots" aria-hidden="true">
        {questions.map((q, i) => (
          <span
            key={q.id}
            className={`cq-rapid-dot ${picks[q.id] ? 'is-done' : ''} ${i === index ? 'is-current' : ''}`}
          />
        ))}
      </div>
      <p className="cvq-kicker">{label}</p>
      <div key={current.id} className="cq-rapid-stage">
        <h2 className="cq-question">{t(current.question)}</h2>
        <p className="cq-desc">{t(current.hint || COPY.gutFeel)}</p>
        <div className="cq-rapid-options">
          {optionsFor(current, persona).map((opt, idx) => (
            <button
              key={opt.key}
              type="button"
              className={`cq-rapid-option cq-anim-item ${picks[current.id] === opt.key ? 'is-selected' : ''}`}
              style={stagger(idx)}
              data-testid="cover-quiz-option"
              onClick={() => pick(opt.key)}
            >
              <span className="cq-rapid-emoji">{opt.icon}</span>
              <span className="cq-rapid-label">{t(opt.label)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="cq-actions">
        <button type="button" className="cq-btn-secondary" data-testid="cover-quiz-back" onClick={back}>
          <ChevronLeft size={18} /> {t(COPY.back)}
        </button>
      </div>
    </div>
  );
};

/** Pick several (C6 D1 D2): `max` caps it, and 「沒有特別／暫時沒有」 stands alone. */
const MultiPick = (props: {
  question: MultiQuestion;
  persona?: CoverPersona;
  value: string[];
  onChange: (next: string[]) => void;
  layout: 'tiles' | 'chips';
}) => {
  const { question, persona, value, onChange, layout } = props;
  const t = useTx();
  const max = question.max || Infinity;
  const counted = value.filter(k => k !== 'none');
  const toggle = (key: string) => {
    if (value.includes(key)) onChange(value.filter(k => k !== key));
    else if (key === 'none') onChange(['none']);
    else if (counted.length < max) onChange([...counted, key]);
  };
  return (
    <div className={layout === 'tiles' ? 'cvq-tile-grid' : 'cvq-chip-row'}>
      {optionsFor(question, persona).map((opt, idx) => {
        const selected = value.includes(opt.key);
        return (
          <button
            key={opt.key}
            type="button"
            className={`${layout === 'tiles' ? 'cvq-tile' : 'cvq-chip'} cq-anim-item${selected ? ' is-selected' : ''}`}
            style={stagger(idx)}
            aria-pressed={selected}
            data-testid={`cover-quiz-${question.id}-option`}
            onClick={() => toggle(opt.key)}
          >
            <span className={layout === 'tiles' ? 'cvq-tile-icon' : undefined} aria-hidden="true">
              {opt.icon}
            </span>{' '}
            <span>{t(opt.label)}</span>
          </button>
        );
      })}
    </div>
  );
};

/** A single-choice question laid out as chips (第二部分). */
const ChoiceRow = (props: { question: GoalQuestion; value?: string; onPick: (key: string) => void }) => {
  const t = useTx();
  return (
    <div className="cvq-choice" data-testid={`cover-quiz-choice-${props.question.id}`}>
      <p className="cvq-choice-label">{t(props.question.question)}</p>
      <div className="cvq-chip-row">
        {props.question.options.map(opt => (
          <button
            key={opt.key}
            type="button"
            className={`cvq-chip${props.value === opt.key ? ' is-selected' : ''}`}
            aria-pressed={props.value === opt.key}
            onClick={() => props.onPick(opt.key)}
          >
            {t(opt.label)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const CoverQuizComponent: React.FC<CoverQuizProps> = ({ location }) => {
  const search = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
  const { token } = parseQuizLink(search);
  // Answers in progress survive coming back to the link (iOS: WhatsApp, Safari reloads) — see quizDraft.
  const draftKey = quizDraftKey('cover', token);
  const [start] = useState(() => coverStartOf(readQuizDraft<CoverDraft>(draftKey)));

  const [step, setStep] = useState(start.step);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const goTo = (next: number) => {
    setDirection(next >= step ? 'forward' : 'back');
    setStep(next);
  };

  // A `?t=` link starts 'loading' and resolves on the server; a bare link
  // (the /b preview) resolves at once and never fetches anything.
  const { link: serverLink, setLink, quiz, reload } = usePublicQuizLink(token, 'cover');
  const [sentAt, setSentAt] = useState<number | null>(start.sentAt);
  const link = linkStateOf(serverLink, sentAt);
  const [lang, setLang] = usePageQuizLang(quiz?.locale, search, start.lang);
  const t = (text: Txt) => text[lang];
  const langBar = <QuizLangSwitcher lang={lang} onChange={setLang} testId="cover-quiz-lang" />;
  const isGuest = link === 'guest';
  const partTwoOnly = quiz?.part === 2;
  // A resumed link returns no quiz, so the adviser's name kept in the draft stands in.
  const adviserName = quiz ? quiz.adviserName : start.adviserName;
  const adviserLabel = t(adviserName ? COPY.adviserNamed(adviserName) : COPY.yourAdviser);

  const [name, setName] = useState(start.name);
  useEffect(() => {
    // A name the client already edited (restored from the draft) is theirs to keep.
    if (quiz?.displayName && !start.hasName) setName(quiz.displayName);
  }, [quiz?.displayName, start]);

  const [persona, setPersona] = useState<CoverPersona | undefined>(start.persona);
  const [role, setRole] = useState<CoverRole | undefined>(start.role);
  const [picks, setPicks] = useState<Record<string, string>>(start.picks);
  const [multi, setMulti] = useState<Record<string, string[]>>(start.multi);
  const [trueFalse, setTrueFalse] = useState<Record<string, boolean>>(start.trueFalse);
  const [saved, setSaved] = useState(start.sentAt !== null);
  const [goals, setGoals] = useState<CoverGoalAnswers>(start.goals);

  const [tfIndex, setTfIndex] = useState(start.tfIndex);

  // The persona tap advances on a short timer like the pick series; one at most.
  const personaTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (personaTimer.current !== null) window.clearTimeout(personaTimer.current);
    },
    []
  );

  // A submit / goals call that failed for a reason other than 410/409: the
  // answers stay in memory and a retry banner re-runs the same handler.
  const { submit, sending, failed } = useQuizSubmit<'finish' | 'report'>(setLink);

  // What is kept, and until when: coverDraftPolicy.
  useQuizDraft(
    draftKey,
    { step, name, lang, persona, role, picks, multi, trueFalse, tfIndex, goals, sentAt, adviserName },
    coverDraftPolicy(link, saved, sentAt)
  );

  if (link === 'loading') {
    return (
      <QuizLangProvider value={lang}>
        <div className="career-quiz-page cover-quiz-page" data-testid="cover-quiz" lang={lang}>
          <div className="career-quiz-container">
            {langBar}
            <div className="cq-card cvq-center" data-testid="cover-quiz-loading">
              {t(L('載入中…', '加载中…', 'Loading…'))}
            </div>
          </div>
        </div>
      </QuizLangProvider>
    );
  }
  if (isTerminalLinkState(link)) {
    return (
      <QuizLangProvider value={lang}>
        <div className="career-quiz-page cover-quiz-page" data-testid="cover-quiz" lang={lang}>
          <div className="career-quiz-container">
            {langBar}
            <LinkMessage state={link} testIdPrefix="cover-quiz" className="cq-card cvq-center" onRetry={reload} />
          </div>
        </div>
      </QuizLangProvider>
    );
  }

  const pick = (id: string, key: string) => setPicks(prev => ({ ...prev, [id]: key }));
  const setMultiFor = (id: string) => (next: string[]) => setMulti(prev => ({ ...prev, [id]: next }));

  const answers: CoverQuizAnswers = {
    persona,
    role,
    picks,
    multi,
    trueFalse
  };
  const signals = coverQuizSignals(answers, lang);
  const report = coverClientReport(answers, lang);
  const animal = COVER_ANIMALS[signals.disc || 'S'];
  const badge = knowledgeBadge(signals.knowledge.score, signals.knowledge.total);
  const displayName = name.trim();

  const chooseRole = (nextRole: CoverRole, next: CoverPersona) => {
    // Answers picked from the other persona's option sets no longer apply; a
    // different role within the same persona keeps them.
    if (persona && persona !== next) {
      setPicks(prev => {
        const rest = { ...prev };
        ['C3', 'E1'].forEach(id => delete rest[id]);
        return rest;
      });
      setMulti({});
      setGoals(EMPTY_GOALS);
    }
    setPersona(next);
    setRole(nextRole);
    if (personaTimer.current !== null) window.clearTimeout(personaTimer.current);
    personaTimer.current = window.setTimeout(() => {
      personaTimer.current = null;
      goTo(partTwoOnly ? GOAL_PICK_STEP : 2);
    }, 280);
  };

  // Sharing is agreed to by submitting — the disclaimer under the button says
  // so — rather than by a checkbox. A guest (preview) link has no one to share
  // with, so nothing is sent; a failure leaves the answers in place with a
  // retry banner (410 → the link has since expired; 409 → already completed).
  const finish = () => {
    if (isGuest) {
      goTo(RESULT_STEP);
      return;
    }
    submit(
      'finish',
      () => submitPublicQuiz(token as string, { answers, version: 2, locale: lang }),
      () => {
        // sentAt first: React 17 does not batch these, and a render with
        // `saved` but no `sentAt` would read as 第二部分-only and drop the draft.
        setSentAt(Date.now());
        setSaved(true);
        goTo(RESULT_STEP);
      }
    );
  };

  const restart = () => {
    setPersona(undefined);
    setRole(undefined);
    setPicks({});
    setMulti({});
    setTrueFalse({});
    setTfIndex(0);
    setGoals(EMPTY_GOALS);
    goTo(0);
  };

  // ── 第二部分 · 理財目標 ─────────────────────────────────────────────────────
  const goalOptions = goalsFor(persona);
  const pickedGoals = goals.picked.map(id => GOALS.find(g => g.id === id)).filter((g): g is GoalDef => Boolean(g));
  const goalDetailsReady = pickedGoals.every(g => g.questions.every(q => goals.details[g.id]?.[q.id]));
  const plan = planGoals(goals, persona, lang);
  const toggleGoal = (id: string) =>
    setGoals(prev =>
      prev.picked.includes(id)
        ? { ...prev, picked: prev.picked.filter(g => g !== id) }
        : prev.picked.length < GOAL_MAX
        ? { ...prev, picked: [...prev.picked, id] }
        : prev
    );
  const answerGoal = (goalId: string, questionId: string, key: string) =>
    setGoals(prev => ({
      ...prev,
      details: { ...prev.details, [goalId]: { ...prev.details[goalId], [questionId]: key } }
    }));
  // A guest's answers are never sent. Sent on its own (`partTwoOnly`), 第二部分
  // is its own submission (persona + role + goals); in-session it rides on the
  // part 1 submission already made in `finish()`, adding just the goals — a
  // 409 GOALS_NOT_ACCEPTED there still shows the report, the goals simply
  // weren't added.
  const showReport = () => {
    if (isGuest) {
      goTo(REPORT_STEP);
      return;
    }
    if (partTwoOnly) {
      submit(
        'report',
        () =>
          submitPublicQuiz(token as string, {
            answers: { persona, role, goals },
            version: 2,
            locale: lang
          }),
        () => {
          setSaved(true);
          goTo(REPORT_STEP);
        }
      );
      return;
    }
    submit(
      'report',
      () => addPublicQuizGoals(token as string, goals),
      () => goTo(REPORT_STEP),
      'proceed'
    );
  };

  // ── 真假挑戰 ────────────────────────────────────────────────────────────────
  const tf = TRUE_FALSE_QUESTIONS[tfIndex];
  const tfAnswered = typeof trueFalse[tf.id] === 'boolean';
  const tfCorrect = trueFalse[tf.id] === tf.answer;

  const inPartTwo = step > RESULT_STEP || (partTwoOnly && step === 1);
  // Sent on its own, 第二部分 counts 身分 as its first step.
  const partTwoTotal = REPORT_STEP - RESULT_STEP + (partTwoOnly ? 1 : 0);
  const partTwoIndex = step === 1 ? 1 : step - RESULT_STEP + (partTwoOnly ? 1 : 0);
  const progress = inPartTwo
    ? Math.round((partTwoIndex / partTwoTotal) * 100)
    : step === 0
    ? 4
    : Math.round((Math.min(step, RESULT_STEP) / RESULT_STEP) * 100);
  const interestCount = (multi.C6 || []).length;
  const familyReady = (multi.D1 || []).length > 0 && (multi.D2 || []).length > 0;
  const serviceReady = SERVICE_QUESTIONS.every(q => picks[q.id]);

  return (
    <QuizLangProvider value={lang}>
      <div className="career-quiz-page cover-quiz-page" data-testid="cover-quiz" lang={lang}>
        <div className="career-quiz-container">
          {langBar}
          <header className="cq-sos-header">
            <div className="cq-sos-brand">
              <div className="cq-sos-logo">PFP</div>
              <div className="cq-sos-brand-copy">
                <span className="cq-sos-brand-title">{t(COPY.brand)}</span>
                <span className="cq-sos-brand-desc">{t(COPY.invites(adviserLabel))}</span>
              </div>
            </div>
            <div className="cq-sos-badge-pill">
              {t(
                step === 0
                  ? partTwoOnly
                    ? COPY.partTwo
                    : COPY.game
                  : inPartTwo
                  ? COPY.partTwo
                  : step >= RESULT_STEP
                  ? COPY.done
                  : COPY.game
              )}
            </div>
          </header>

          <div className="cq-progress">
            <div
              className="cq-progress-track"
              role="progressbar"
              aria-valuenow={step}
              aria-valuemin={0}
              aria-valuemax={RESULT_STEP}
              aria-label={t(COPY.progress)}
            >
              <div className="cq-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="cq-progress-meta">
              <span className="cq-progress-label">{t(STEP_LABELS[step])}</span>
              {/* No "3 / 18" anywhere: how many questions are left is never shown, only the bar. */}
              <span className="cq-progress-count">
                {step === 0
                  ? partTwoOnly
                    ? t(COPY.aboutOneMinute)
                    : t(COPY.aboutTwoMinutes)
                  : step >= RESULT_STEP && !inPartTwo
                  ? '🐾'
                  : null}
              </span>
            </div>
          </div>

          <main className="cq-stage">
            <div key={step} className={`cq-step-frame ${direction === 'forward' ? 'is-forward' : 'is-back'}`}>
              {step === 0 && (
                <div className="cq-card cq-card-welcome">
                  <h1 className="cq-title">{t(partTwoOnly ? COPY.partTwoTitle : COPY.partOneTitle)}</h1>
                  <p className="cq-subtitle">{t(partTwoOnly ? COPY.partTwoSubtitle : COPY.partOneSubtitle)}</p>
                  <div className="cvq-value" data-testid="cover-quiz-value">
                    <span className="cvq-value-heading">{t(COPY.youWillGet)}</span>
                    <ul>
                      {(partTwoOnly ? PART_TWO_VALUES : WELCOME_VALUES).map(v => (
                        <li key={v.title.en} className={`cvq-value-item is-${v.tone}`}>
                          <span className="cvq-value-icon" aria-hidden="true">
                            {v.icon}
                          </span>
                          <span className="cvq-value-copy">
                            <strong>{t(v.title)}</strong>
                            <em>{t(v.desc)}</em>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* On an invitation the name is the one the adviser sent it with — it is
                    not sent back with the answers, so it is not editable here. A preview
                    saves nothing, so there the name only personalises the report. */}
                  {!isGuest && displayName && (
                    <p className="cq-welcome-input-box" data-testid="cover-quiz-greeting" style={{ fontWeight: 600 }}>
                      {t(COPY.hello(displayName))}
                    </p>
                  )}
                  {isGuest && (
                    <div className="cq-welcome-input-box">
                      <label htmlFor="cvq-name">{t(COPY.nameLabel)}</label>
                      <div className="cq-input-wrap">
                        <User size={16} className="cq-input-icon" />
                        <input
                          id="cvq-name"
                          type="text"
                          placeholder={t(COPY.namePlaceholder)}
                          value={name}
                          onChange={e => setName(e.target.value)}
                          className="cq-input"
                        />
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="cq-btn-primary cq-btn-large"
                    data-testid="cover-quiz-start"
                    onClick={() => goTo(1)}
                  >
                    {t(COPY.start)} <ChevronRight size={18} />
                  </button>
                </div>
              )}

              {step === 1 && (
                <div className="cq-card" data-testid="cover-quiz-persona">
                  <h2 className="cq-question">{t(COPY.roleTitle)}</h2>
                  <p className="cq-desc">{t(COPY.roleDesc)}</p>
                  <div className="cvq-role-grid">
                    {ROLE_OPTIONS.map((opt, idx) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={`cvq-contact cvq-persona cq-anim-item${role === opt.key ? ' is-selected' : ''}`}
                        style={stagger(idx)}
                        aria-pressed={role === opt.key}
                        data-testid="cover-quiz-persona-option"
                        data-role={opt.key}
                        data-persona={opt.persona}
                        onClick={() => chooseRole(opt.key, opt.persona)}
                      >
                        <span className="cvq-tile-icon">{opt.icon}</span>
                        <span className="cvq-contact-copy">
                          <strong>{t(opt.label)}</strong>
                          <small>{t(opt.hint)}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(0)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <PickSeries
                  questions={PERSONALITY_QUESTIONS}
                  persona={persona}
                  picks={picks}
                  onPick={pick}
                  onDone={() => goTo(3)}
                  onBack={() => goTo(1)}
                  label={t(COPY.personalityLabel)}
                />
              )}

              {step === 3 && (
                <div className="cq-card" data-testid="cover-quiz-truefalse">
                  <p className="cvq-kicker">{t(COPY.tfKicker)}</p>
                  {tfIndex === 0 && (
                    <p className="cq-desc" data-testid="cover-quiz-tf-intro">
                      {t(COPY.tfIntro)}
                    </p>
                  )}
                  <div className="cvq-statement">
                    <h2 className="cq-question">{t(COPY.quote(t(tf.statement)))}</h2>
                  </div>
                  <div className="cvq-tf-row">
                    {[true, false].map(value => {
                      const chosen = trueFalse[tf.id] === value;
                      const state = !tfAnswered
                        ? ''
                        : value === tf.answer
                        ? ' is-answer'
                        : chosen
                        ? ' is-wrong'
                        : ' is-dim';
                      return (
                        <button
                          key={String(value)}
                          type="button"
                          className={`cvq-tf-btn${state}`}
                          data-testid={`cover-quiz-tf-${value ? 'true' : 'false'}`}
                          disabled={tfAnswered}
                          onClick={() => setTrueFalse(prev => ({ ...prev, [tf.id]: value }))}
                        >
                          {t(value ? COPY.tfTrue : COPY.tfFalse)}
                        </button>
                      );
                    })}
                  </div>
                  {tfAnswered && (
                    <div
                      className={`cvq-reveal ${tfCorrect ? 'is-correct' : 'is-wrong'}`}
                      data-testid="cover-quiz-reveal"
                    >
                      {/* G3 — a burst of confetti for a right answer, a puzzled bird for a wrong one. */}
                      {tfCorrect ? (
                        <div className="cvq-confetti" aria-hidden="true" data-testid="cover-quiz-confetti">
                          {CONFETTI.map((piece, i) => (
                            <span key={i} style={{ left: `${8 + i * 12}%`, animationDelay: `${i * 40}ms` }}>
                              {piece}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="cvq-tilt" aria-hidden="true" data-testid="cover-quiz-tilt">
                          🐦
                        </span>
                      )}
                      <strong>{t(tfCorrect ? COPY.correct : COPY.almost(tf.answer))}</strong>
                      <p>{t(tf.fact)}</p>
                    </div>
                  )}
                  <div className="cq-actions">
                    <button
                      type="button"
                      className="cq-btn-secondary"
                      onClick={() => (tfIndex > 0 ? setTfIndex(tfIndex - 1) : goTo(2))}
                    >
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={!tfAnswered}
                      data-testid="cover-quiz-next"
                      onClick={() => (tfIndex < TRUE_FALSE_QUESTIONS.length - 1 ? setTfIndex(tfIndex + 1) : goTo(4))}
                    >
                      {t(tfIndex < TRUE_FALSE_QUESTIONS.length - 1 ? COPY.nextQuestion : COPY.continue)}{' '}
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <PickSeries
                  questions={LIFE_QUESTIONS}
                  persona={persona}
                  picks={picks}
                  onPick={pick}
                  onDone={() => goTo(5)}
                  onBack={() => goTo(3)}
                  label={t(COPY.lifeLabel)}
                />
              )}

              {step === 5 && (
                <PickSeries
                  questions={DECISION_QUESTIONS}
                  persona={persona}
                  picks={picks}
                  onPick={pick}
                  onDone={() => goTo(6)}
                  onBack={() => goTo(4)}
                  label={t(COPY.decisionLabel)}
                />
              )}

              {step === 6 && (
                <div className="cq-card" data-testid="cover-quiz-interests">
                  <h2 className="cq-question">{t(INTEREST_QUESTION.question)}</h2>
                  <p className="cq-desc">{t(COPY.pickUpTo(INTEREST_QUESTION.max || 0, interestCount))}</p>
                  <MultiPick
                    question={INTEREST_QUESTION}
                    persona={persona}
                    value={multi.C6 || []}
                    onChange={setMultiFor('C6')}
                    layout="tiles"
                  />
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(5)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={interestCount === 0}
                      data-testid="cover-quiz-next"
                      onClick={() => goTo(FAMILY_STEP)}
                    >
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {step === FAMILY_STEP && (
                <div className="cq-card" data-testid="cover-quiz-family">
                  {FAMILY_QUESTIONS.map(question => (
                    <div key={question.id} className="cvq-family-block">
                      <h2 className="cq-question">{t(question.question)}</h2>
                      <p className="cq-desc">{t(COPY.pickSeveral)}</p>
                      <MultiPick
                        question={question}
                        persona={persona}
                        value={multi[question.id] || []}
                        onChange={setMultiFor(question.id)}
                        layout="chips"
                      />
                    </div>
                  ))}
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(FAMILY_STEP - 1)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={!familyReady}
                      data-testid="cover-quiz-next"
                      onClick={() => goTo(SERVICE_STEP)}
                    >
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {step === SERVICE_STEP && (
                <div className="cq-card" data-testid="cover-quiz-service">
                  <h2 className="cq-question">{t(COPY.serviceTitle)}</h2>
                  <p className="cq-desc">{t(COPY.serviceDesc(adviserLabel))}</p>
                  {SERVICE_QUESTIONS.map(question => (
                    <div key={question.id} className="cvq-choice" data-testid={`cover-quiz-choice-${question.id}`}>
                      <p className="cvq-choice-label">{t(question.question)}</p>
                      <div className="cvq-chip-row">
                        {optionsFor(question, persona).map(opt => (
                          <button
                            key={opt.key}
                            type="button"
                            className={`cvq-chip${picks[question.id] === opt.key ? ' is-selected' : ''}`}
                            aria-pressed={picks[question.id] === opt.key}
                            onClick={() => pick(question.id, opt.key)}
                          >
                            <span aria-hidden="true">{opt.icon}</span> {t(opt.label)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!isGuest && (
                    <p className="cvq-consent-note" data-testid="cover-quiz-consent-note">
                      {t(COPY.consentReport(adviserLabel))}
                    </p>
                  )}
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(FAMILY_STEP)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={!serviceReady || sending}
                      data-testid="cover-quiz-finish"
                      onClick={finish}
                    >
                      {t(COPY.seeReport)}
                    </button>
                  </div>
                  {failed === 'finish' && (
                    <RetryBanner onRetry={finish} disabled={sending} testIdPrefix="cover-quiz" className="cvq-saved" />
                  )}
                </div>
              )}

              {step === RESULT_STEP && (
                <div className="cq-card cq-card-result" data-testid="cover-quiz-result">
                  <div className="cvq-result-capture">
                    <div className="cvq-report-hero cq-anim-item">
                      <span className="cvq-report-kicker">{t(COPY.reportOf(displayName))}</span>
                      <p className="cvq-report-headline" data-testid="cover-quiz-headline">
                        {report.headline}
                      </p>
                      <div className="cvq-report-stats">
                        <span className="cvq-report-stat">
                          <span aria-hidden="true">{badge.emoji}</span>
                          <span>
                            <small>{t(COPY.knowledge)}</small>
                            <strong>
                              {signals.knowledge.score} / {signals.knowledge.total} · {t(badge.name)}
                            </strong>
                          </span>
                        </span>
                        <span className="cvq-report-stat">
                          <span aria-hidden="true">{animal.emoji}</span>
                          <span>
                            <small>{t(COPY.personality)}</small>
                            <strong data-testid="cover-quiz-animal">{t(animal.name)}</strong>
                          </span>
                        </span>
                      </div>
                    </div>

                    {report.priorities.length > 0 && (
                      <section
                        className="cvq-report-section cq-anim-item"
                        style={stagger(1)}
                        data-testid="cover-quiz-priorities"
                      >
                        <h3>{t(COPY.priorities)}</h3>
                        <ol className="cvq-priority-list">
                          {report.priorities.map((p, i) => (
                            <li key={p.key} className="cvq-priority">
                              <span className="cvq-priority-rank">{i + 1}</span>
                              <span className="cvq-priority-copy">
                                <strong>
                                  {p.icon} {p.title}
                                </strong>
                                <span>{p.why}</span>
                              </span>
                            </li>
                          ))}
                        </ol>
                      </section>
                    )}

                    <section className="cvq-report-section cq-anim-item" style={stagger(2)}>
                      <h3>
                        {animal.emoji} {t(COPY.yourPersonality)}
                        {t(animal.name)}
                        <DiscLetterBadge letter={signals.disc || 'S'} />
                      </h3>
                      <p className="cvq-report-text">{t(animal.desc)}</p>
                      <ul className="cvq-tip-list">
                        <li>
                          <span aria-hidden="true">✨</span>
                          <span>{t(animal.tip)}</span>
                        </li>
                        {report.tips.map(tip => (
                          <li key={tip.key}>
                            <span aria-hidden="true">{tip.icon}</span>
                            <span>{tip.text}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  </div>

                  {signals.service.length > 0 && (
                    <section className="cvq-report-section cvq-report-service" data-testid="cover-quiz-service-summary">
                      <h3>{t(COPY.serviceHeading)}</h3>
                      <ul className="cvq-service-list">
                        {signals.service.map(item => (
                          <li key={item.id}>
                            <small>{t(item.short)}</small>
                            <strong>
                              {item.option.icon} {t(item.option.label)}
                            </strong>
                          </li>
                        ))}
                      </ul>
                      {saved && <p className="cvq-report-text">{t(COPY.serviceSaved(adviserLabel))}</p>}
                    </section>
                  )}

                  <div className={`cvq-saved${saved ? ' is-saved' : ''}`} data-testid="cover-quiz-saved">
                    {saved ? <Check size={18} /> : null}
                    <span>{t(saved ? COPY.sharedPartOne(adviserLabel) : COPY.trial)}</span>
                  </div>

                  {/* 第二部分 is optional: the report above already stands on its own. */}
                  <div className="cvq-part-two" data-testid="cover-quiz-part-two">
                    <strong>{t(COPY.partTwoAsk)}</strong>
                    <span>{t(report.dream ? COPY.partTwoPitchDream(report.dream.label) : COPY.partTwoPitch)}</span>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      data-testid="cover-quiz-part-two-start"
                      onClick={() => goTo(GOAL_PICK_STEP)}
                    >
                      {t(COPY.startPartTwo)} <ChevronRight size={18} />
                    </button>
                  </div>

                  <button type="button" className="cq-btn-secondary cvq-restart" onClick={restart}>
                    {t(COPY.restart)}
                  </button>
                </div>
              )}

              {step === GOAL_PICK_STEP && (
                <div className="cq-card" data-testid="cover-quiz-goals">
                  <h2 className="cq-question">{t(COPY.goalsTitle)}</h2>
                  <p className="cq-desc">{t(COPY.pickUpTo(GOAL_MAX, goals.picked.length))}</p>
                  <div className="cvq-tile-grid">
                    {goalOptions.map(goal => {
                      const selected = goals.picked.includes(goal.id);
                      return (
                        <button
                          key={goal.id}
                          type="button"
                          className={`cvq-tile${selected ? ' is-selected' : ''}`}
                          aria-pressed={selected}
                          data-testid="cover-quiz-goal-option"
                          data-goal={goal.id}
                          onClick={() => toggleGoal(goal.id)}
                        >
                          <span className="cvq-tile-icon" aria-hidden="true">
                            {goal.icon}
                          </span>
                          <span>{goalLabel(goal, persona, lang)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="cq-actions">
                    <button
                      type="button"
                      className="cq-btn-secondary"
                      onClick={() => goTo(partTwoOnly ? 1 : RESULT_STEP)}
                    >
                      <ChevronLeft size={18} /> {t(partTwoOnly ? COPY.back : COPY.backToResult)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={goals.picked.length === 0}
                      data-testid="cover-quiz-next"
                      onClick={() => goTo(GOAL_DETAIL_STEP)}
                    >
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {step === GOAL_DETAIL_STEP && (
                <div className="cq-card" data-testid="cover-quiz-goal-details">
                  {pickedGoals.map(goal => (
                    <div key={goal.id} className="cvq-family-block" data-goal={goal.id}>
                      <h2 className="cq-question">
                        {goal.icon} {goalLabel(goal, persona, lang)}
                      </h2>
                      {goal.questions.map(question => (
                        <ChoiceRow
                          key={question.id}
                          question={question}
                          value={goals.details[goal.id]?.[question.id]}
                          onPick={key => answerGoal(goal.id, question.id, key)}
                        />
                      ))}
                    </div>
                  ))}
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(GOAL_PICK_STEP)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={!goalDetailsReady}
                      data-testid="cover-quiz-next"
                      onClick={() => goTo(GOAL_SAVING_STEP)}
                    >
                      {t(COPY.next)} <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}

              {step === GOAL_SAVING_STEP && (
                <div className="cq-card" data-testid="cover-quiz-goal-saving">
                  <ChoiceRow
                    question={SAVED_QUESTION}
                    value={goals.saved}
                    onPick={key => setGoals(prev => ({ ...prev, saved: key }))}
                  />
                  <ChoiceRow
                    question={INCOME_QUESTION}
                    value={goals.income}
                    onPick={key => setGoals(prev => ({ ...prev, income: key }))}
                  />
                  <ChoiceRow
                    question={MONTHLY_QUESTION}
                    value={goals.monthly}
                    onPick={key => setGoals(prev => ({ ...prev, monthly: key }))}
                  />
                  {partTwoOnly && !isGuest && (
                    <p className="cvq-consent-note" data-testid="cover-quiz-consent-note">
                      {t(COPY.consentGoals(adviserLabel))}
                    </p>
                  )}
                  <div className="cq-actions">
                    <button type="button" className="cq-btn-secondary" onClick={() => goTo(GOAL_DETAIL_STEP)}>
                      <ChevronLeft size={18} /> {t(COPY.back)}
                    </button>
                    <button
                      type="button"
                      className="cq-btn-primary"
                      disabled={!goals.saved || !goals.income || !goals.monthly || sending}
                      data-testid="cover-quiz-report"
                      onClick={showReport}
                    >
                      {t(COPY.seeGoalReport)}
                    </button>
                  </div>
                  {failed === 'report' && (
                    <RetryBanner
                      onRetry={showReport}
                      disabled={sending}
                      testIdPrefix="cover-quiz"
                      className="cvq-saved"
                    />
                  )}
                </div>
              )}

              {step === REPORT_STEP && plan && (
                <div className="cq-card cq-card-result" data-testid="cover-quiz-goal-report">
                  <h2 className="cq-question">{t(COPY.goalReportOf(displayName))}</h2>
                  {plan.items.map(item => (
                    <div key={item.id} className="cvq-goal-card" data-testid="cover-quiz-goal-item" data-goal={item.id}>
                      <strong className="cvq-goal-title">
                        {item.icon} {item.label}
                      </strong>
                      <small>{item.basis}</small>
                      <div className="cvq-goal-grid">
                        <span>{t(COPY.today)}</span>
                        <strong>
                          {t(COPY.about)} {formatWan(item.todayLow, lang)}–{formatWan(item.todayHigh, lang)}
                        </strong>
                        <span>{t(COPY.inYears(item.years))}</span>
                        <strong>
                          {t(COPY.about)} {formatWan(item.futureLow, lang)}–{formatWan(item.futureHigh, lang)}
                        </strong>
                        <span>{t(COPY.monthly)}</span>
                        <strong className="cvq-goal-monthly">
                          {item.monthlyHigh <= 0
                            ? t(COPY.enough)
                            : `HK$ ${formatMonthly(item.monthlyLow)}–${formatMonthly(item.monthlyHigh)}`}
                        </strong>
                      </div>
                      {item.facts.length > 0 && (
                        <div className="cvq-goal-note" data-testid="cover-quiz-goal-facts">
                          <span className="cvq-goal-note-title">{t(COPY.facts)}</span>
                          <ul>
                            {item.facts.map(fact => (
                              <li key={fact}>{fact}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.typical && (
                        <div className="cvq-goal-note is-typical" data-testid="cover-quiz-goal-typical">
                          <span className="cvq-goal-note-title">{t(COPY.typical)}</span>
                          <p>{item.typical}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {plan.coveragePct !== null && (
                    <div className="cvq-coverage" data-testid="cover-quiz-coverage">
                      <span>{t(COPY.coverage)}</span>
                      <strong>{plan.coveragePct}%</strong>
                      <div className="cvq-coverage-bar" aria-hidden="true">
                        <div style={{ width: `${plan.coveragePct}%` }} />
                      </div>
                    </div>
                  )}
                  {plan.income !== null && (
                    <div className="cvq-saving-rate" data-testid="cover-quiz-saving-rate">
                      <strong className="cvq-goal-title">{t(COPY.savingRate)}</strong>
                      <div className="cvq-goal-grid">
                        {plan.savingRatePct !== null && (
                          <>
                            <span>{t(COPY.savingNow)}</span>
                            <strong>{t(COPY.ofIncome(plan.savingRatePct))}</strong>
                          </>
                        )}
                        {plan.neededRatePct !== null && (
                          <>
                            <span>{t(COPY.goalsNeed)}</span>
                            <strong>{t(COPY.ofIncome(plan.neededRatePct))}</strong>
                          </>
                        )}
                        <span>{t(COPY.usual)}</span>
                        <strong>{t(COPY.usualRate(SUGGESTED_SAVING_RATE))}</strong>
                      </div>
                    </div>
                  )}
                  <p className="cvq-disclaimer">
                    {t(COPY.disclaimer(Math.round(DEMO_INFLATION * 100), Math.round(DEMO_RETURN * 100)))}
                  </p>
                  <div className={`cvq-saved${saved ? ' is-saved' : ''}`}>
                    {saved ? <Check size={18} /> : null}
                    <span>{t(saved ? COPY.sharedGoals(adviserLabel) : COPY.goalsLocal)}</span>
                  </div>
                  <button
                    type="button"
                    className="cq-btn-secondary cvq-restart"
                    onClick={() => goTo(partTwoOnly ? GOAL_PICK_STEP : RESULT_STEP)}
                  >
                    {t(partTwoOnly ? COPY.repick : COPY.backToReport)}
                  </button>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </QuizLangProvider>
  );
};

export const CoverQuiz = withRouter(CoverQuizComponent);
export default CoverQuiz;
